import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Paper,
  Typography,
  Grid,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Switch,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import UserDirectory from "./UserDirectory";
import ImportCustomersDialog from "./import/customer/ImportCustomersDialog";
import AddUserDialog from "./AddUserDialog";
import UserManagementTabs from "./UserManagementTabs";
import { personas, defaultStorefronts } from "./test-data";

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchCategory, setSearchCategory] = useState("lastName");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedStorefront, setSelectedStorefront] = useState("fedirect-us");
  const [selectedPersonas, setSelectedPersonas] = useState([]);
  const [showActiveUsers, setShowActiveUsers] = useState(false);
  const [sortBy, setSortBy] = useState("lastName");

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    // TODO: Implement search functionality
  };

  const handleSearch = () => {
    // TODO: Implement search functionality
    console.log(`Searching ${searchCategory}: ${searchTerm}`);
  };

  const handleCategoryChange = (category) => {
    setSearchCategory(category);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const filterOptions = [
    { label: "Last Name", value: "lastName" },
    { label: "First Name", value: "firstName" },
    { label: "Email", value: "email" },
  ];

  return (
    <UserManagementTabs subtitle="Manage and configure user accounts, permissions, and access">
      {/* Header Section - only for All Users tab */}
      <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Grid
          container
          spacing={{ xs: 1, md: 2 }}
          alignItems="center"
          justifyContent="space-between"
        >
          {/* Search Bar */}
          <Grid
            item
            xs={12}
            md={12}
            lg={6}
            sx={{ width: { xs: "100%", lg: "auto" } }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexDirection: { xs: "column", lg: "row" },
                flexWrap: "wrap",
              }}
            >
              <FormControl
                size="small"
                sx={{ minWidth: { xs: "100%", lg: 150 } }}
              >
                <InputLabel sx={{ fontSize: "1.3rem", color: "#666" }}>
                  Search By
                </InputLabel>

                <Select
                  value={searchCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  label="Search By"
                  sx={{
                    fontSize: "1.3rem",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#ddd",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1976d2",
                    },
                  }}
                >
                  {filterOptions.map((option) => (
                    <MenuItem value={option.value} sx={{ fontSize: "1.3rem" }}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                placeholder={`Search by ${searchCategory}...`}
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                sx={{
                  flex: { xs: "none", lg: "none" },
                  width: { xs: "100%", lg: "300px", xl: "400px" },

                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    fontSize: "1.3rem",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#ddd",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1976d2",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1976d2",
                    },
                    "& .MuiInputBase-input": {
                      height: "40px",
                    },
                  },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#666", fontSize: "20px" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={clearSearch}
                          sx={{ color: "#666" }}
                        >
                          <CloseIcon sx={{ fontSize: "18px" }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={!searchTerm.trim()}
                sx={{
                  height: { xs: "auto", lg: "40px" },
                  fontSize: "1.3rem",
                  fontWeight: "500",
                  minWidth: { xs: "100%", lg: "80px" },
                  width: { xs: "100%", lg: "auto" },
                  "&:disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#999",
                  },
                }}
              >
                Search
              </Button>
            </Box>
          </Grid>

          {/* Action Buttons */}
          <Grid
            item
            xs={12}
            md={12}
            lg={6}
            sx={{ width: { xs: "100%", lg: "auto" } }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: { md: "flex-start", lg: "flex-end" },
                flexWrap: "wrap",
                mt: { md: 2, lg: 0 },
                flexDirection: { md: "column", lg: "row" },
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setShowFilters(!showFilters)}
                startIcon={<FilterIcon />}
                size="medium"
                sx={{
                  fontSize: "1.3rem",
                  width: { xs: "100%", lg: "auto" },
                }}
              >
                Filters
              </Button>

              <AddUserDialog />

              <ImportCustomersDialog
                onApply={(payload) => console.log("Import apply", payload)}
                allUsers={[]}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Filter Section */}
        <Collapse in={showFilters}>
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: "white",
              borderRadius: 1,
              border: "1px solid #e0e0e0",
            }}
          >
            {/* Filter Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 2,
                pb: 1,
                borderBottom: "2px solid #1976d2",
              }}
            >
              <FilterIcon sx={{ color: "#1976d2", mr: 1 }} />

              <Typography
                variant="h6"
                sx={{ fontWeight: "600", color: "#1976d2" }}
              >
                Active Filters
              </Typography>
            </Box>

            {/* Filter Container */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                alignItems: "center",
              }}
            >
              {/* User Status */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: "200px",
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "500",
                    mr: 1,
                    color: "#666",
                    fontSize: "16px",
                  }}
                >
                  Status:
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={showActiveUsers}
                      onChange={(e) => setShowActiveUsers(e.target.checked)}
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#2e7d32",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#2e7d32",
                          },
                      }}
                    />
                  }
                  label="Active Users Only"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "1.3rem",
                      color: "#333",
                    },
                  }}
                />
              </Box>

              {/* Sort Options */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: "200px",
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "500",
                    mr: 1,
                    color: "#666",
                    fontSize: "16px",
                  }}
                >
                  Sort:
                </Typography>

                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Chip
                    label="First Name"
                    variant={sortBy === "firstName" ? "filled" : "outlined"}
                    color={sortBy === "firstName" ? "primary" : "default"}
                    onClick={() => setSortBy("firstName")}
                    clickable
                    size="small"
                    sx={{
                      fontSize: "1.1rem",
                      height: "28px",
                      "&:hover": {
                        backgroundColor:
                          sortBy === "firstName" ? "#e3f2fd" : "#f5f5f5",
                        color: sortBy === "firstName" ? "#1976d2" : "inherit",
                      },
                    }}
                  />

                  <Chip
                    label="Last Name"
                    variant={sortBy === "lastName" ? "filled" : "outlined"}
                    color={sortBy === "lastName" ? "primary" : "default"}
                    onClick={() => setSortBy("lastName")}
                    clickable
                    size="small"
                    sx={{
                      fontSize: "1.1rem",
                      height: "28px",
                      "&:hover": {
                        backgroundColor:
                          sortBy === "lastName" ? "#e3f2fd" : "#f5f5f5",
                        color: sortBy === "lastName" ? "#1976d2" : "inherit",
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Storefront Filter */}
              <FormControl
                size="small"
                sx={{ minWidth: { xs: "100%", sm: 200 }, flexShrink: 0 }}
              >
                <InputLabel sx={{ fontSize: "1.3rem", color: "#666" }}>
                  Storefront
                </InputLabel>

                <Select
                  value={selectedStorefront}
                  onChange={(e) => setSelectedStorefront(e.target.value)}
                  label="Storefront"
                  sx={{
                    fontSize: "1.3rem",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#ddd",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1976d2",
                    },
                  }}
                >
                  {defaultStorefronts.map((storefront) => (
                    <MenuItem
                      key={storefront.value}
                      value={storefront.value}
                      sx={{ fontSize: "1.3rem" }}
                    >
                      {storefront.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Persona Filter */}
              <FormControl
                size="small"
                sx={{ minWidth: { xs: "100%", sm: 200 }, flexShrink: 0 }}
              >
                <InputLabel sx={{ fontSize: "1.3rem", color: "#666" }}>
                  Persona
                </InputLabel>

                <Select
                  multiple
                  value={selectedPersonas}
                  onChange={(e) => setSelectedPersonas(e.target.value)}
                  label="Persona"
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.length === 0 ? (
                        <Typography sx={{ color: "#999", fontSize: "1.3rem" }}>
                          Select personas...
                        </Typography>
                      ) : (
                        selected.map((value) => (
                          <Chip
                            key={value}
                            label={value}
                            size="small"
                            sx={{
                              fontSize: "11px",
                              backgroundColor: "#1976d2",
                              color: "white",
                              height: "24px",
                            }}
                          />
                        ))
                      )}
                    </Box>
                  )}
                  sx={{
                    fontSize: "1.3rem",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#ddd",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1976d2",
                    },
                  }}
                >
                  <MenuItem value="all" sx={{ fontSize: "1.3rem" }}>
                    <Checkbox
                      checked={selectedPersonas.length === 0}
                      size="small"
                    />

                    <Typography sx={{ fontSize: "1.3rem" }}>
                      All Personas
                    </Typography>
                  </MenuItem>

                  {personas.map((persona) => (
                    <MenuItem
                      key={persona}
                      value={persona}
                      sx={{ fontSize: "1.3rem" }}
                    >
                      <Checkbox
                        checked={selectedPersonas.includes(persona)}
                        size="small"
                      />

                      <Typography sx={{ fontSize: "1.3rem" }}>
                        {persona}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Collapse>
      </Paper>

      {/* Main Content Area */}
      <UserDirectory />
    </UserManagementTabs>
  );
};

export default UserManagement;
